import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./header.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@/components/ui-components/icon";
import { PopupMenu, type PopupMenuItem } from "@/components/ui-components/popup-menu";
import { LanguagePicker } from "@/components/layout-components/language-picker";
import { SearchBox } from "@/components/layout-components/search-box";
import type { Product } from "@/data/products";
import type { Category } from "@/data/categories";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";
import { assetUrl } from "@/utils/assets";
import { formatCurrency } from "@/utils/format";
import { useAuthStore } from "@/app/state/auth-store";
import { authApi } from "@/services/auth/auth-api";
import { storefrontApi } from "@/services/account/storefront-api";

interface Props {
    locale: LocaleCode;
    appearance: "light" | "dark";
    menuOpen: boolean;
    cartOpen: boolean;
    cartCount: number;
    cartTotal: number;
    search: string;
    searchResults: Product[];
    t: (key: string, values?: InterpolationValues) => string;
    onLocaleChange: (locale: LocaleCode) => void;
    onAppearanceToggle: () => void;
    onSearchChange: (query: string) => void;
    onProductSelect: (product: Product) => void;
    onMenuOpen: () => void;
    onCartOpen: () => void;
    categories?: Category[];
    belowFilters?: React.ReactNode;
}

export function Header(props: Props) {
    const headerRef = useRef<HTMLElement>(null);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const accountButtonRef = useRef<HTMLButtonElement>(null);
    const notificationsMenuRef = useRef<HTMLDivElement>(null);
    const notificationsButtonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const allCategories = (props.categories ?? []).flatMap(category => [category, ...(category.children ?? [])]);
    const primaryCategories = [...new Map(allCategories.filter(category => category.count > 0).map(category => [category.id, category])).values()].slice(0, 3);
    const filters = ["discover", "realProducts", "newArrivals", "offers", ...primaryCategories.map(category => `category:${category.id}`)];
    const user = useAuthStore(state => state.user);
    const signOutLocal = useAuthStore(state => state.signOutLocal);
    const submenuPlacement = "right";
    const [accountOpen, setAccountOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [signingOut, setSigningOut] = React.useState(false);
    const notificationsMenuId = useId();
    const menuOffset = React.useMemo(() => {
        return 0;
    }, [props.appearance, accountOpen]);
    const notificationsQuery = useQuery({
        queryKey: ["account", "notifications"],
        queryFn: () => storefrontApi.listNotifications(),
        enabled: notificationsOpen && Boolean(user),
        staleTime: 30_000
    });
    const recentNotifications = useMemo(() => (notificationsQuery.data ?? [])
        .filter(notification => !notification.archivedAt)
        .sort((first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime())
        .slice(0, 4), [notificationsQuery.data]);

    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            await authApi.signOut();
        } catch {
            // Best-effort: clear local session regardless of network/API errors.
        } finally {
            signOutLocal();
            setAccountOpen(false);
            setSigningOut(false);
        }
    };

    const themeMenuItem: PopupMenuItem = {
        id: "theme-toggle",
        icon: <Icon name={props.appearance === "dark" ? "sun" : "moon"} />,
        label: props.appearance === "dark" ? props.t("layout.header.lightTheme") : props.t("layout.header.darkTheme"),
        onSelect: () => {
            setAccountOpen(false);
            props.onAppearanceToggle();
        }
    };

    const accountMenuItems: PopupMenuItem[] = user ? [
        { id: "cart", label: props.t("layout.header.shoppingCart"), icon: <Icon name="cart" />, onSelect: () => { setAccountOpen(false); props.onCartOpen(); } },
        themeMenuItem,
        { id: "account", label: props.t("layout.header.account"), icon: <Icon name="user" />, children: [
            { id: "orders", label: props.t("layout.header.myOrders"), icon: <Icon name="bag" />, to: `/${props.locale}/my-orders` },
            { id: "notifications", label: props.t("layout.header.notifications"), icon: <Icon name="bell" />, to: `/${props.locale}/notifications` },
            { id: "settings", label: props.t("layout.header.settings"), icon: <Icon name="grid" />, to: `/${props.locale}/account-settings` },
        ] },
        { id: "sign-out", label: props.t("layout.header.signOut"), icon: <Icon name="x" />, danger: true, onSelect: handleSignOut }
    ] : [
        themeMenuItem,
        { id: "account", label: props.t("layout.header.account"), icon: <Icon name="user" />, children: [
            { id: "sign-in", label: props.t("layout.header.signIn"), icon: <Icon name="user" />, to: `/${props.locale}/sign-in` },
            { id: "sign-up", label: props.t("layout.header.signUp"), icon: <Icon name="home" />, to: `/${props.locale}/sign-up` },
        ] },
    ];

    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const syncStickyOffset = () => { const height = header.offsetHeight; const rootSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16; if (height > 20) document.documentElement.style.setProperty("--site-sticky-offset", `${Number(((Math.ceil(height) + 10) / rootSize).toFixed(4))}rem`); };
        syncStickyOffset();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncStickyOffset);
        observer?.observe(header);
        window.addEventListener("resize", syncStickyOffset);
        return () => { observer?.disconnect(); window.removeEventListener("resize", syncStickyOffset); };
    }, []);

    useEffect(() => {
        if (!accountOpen && !notificationsOpen) return;

        const pointerDown = (event: PointerEvent) => {
            if (event.defaultPrevented) return;

            const target = event.target as Node | null;
            if (!target) return;

            const insideButton = accountButtonRef.current?.contains(target);
            const insidePopup = Array.from(document.querySelectorAll<HTMLElement>("[data-popup-root][data-popup-open='true']")).some(popup => popup.contains(target));
            const insideNotificationsButton = notificationsButtonRef.current?.contains(target);
            const insideNotificationsMenu = notificationsMenuRef.current?.contains(target);

            if (accountOpen && !insideButton && !insidePopup) {
                setAccountOpen(false);
            }
            if (notificationsOpen && !insideNotificationsButton && !insideNotificationsMenu) setNotificationsOpen(false);
        };

        document.addEventListener("pointerdown", pointerDown);
        return () => document.removeEventListener("pointerdown", pointerDown);
    }, [accountOpen, notificationsOpen]);

    const handleBrandClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        void event;
        navigate(`/${props.locale}`);
    };
    const handleBrandKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(`/${props.locale}`);
        }
    };

    return (
        <>
            <div className="topbar">
                <div className="shell">
                    <div className="topbar__left"><strong>Pitango Market</strong><span>{props.t("layout.topbar.delivery")}</span></div>
                    <div className="topbar__right"><Link to={`/${props.locale}/my-orders`}>{props.t("layout.topbar.orders")}</Link><Link to={`/${props.locale}/about`}>{props.t("layout.topbar.about")}</Link><Link to={`/${props.locale}/about`}>{props.t("layout.topbar.support")}</Link></div>
                </div>
            </div>
            <header className="header" ref={headerRef}>
                <div className="shell">
                    <div className="header__inner">
                        <button className="hamburger" type="button" aria-label={props.t("common.accessibility.openMenu")} aria-controls="shop-menu-drawer" aria-expanded={props.menuOpen} onClick={props.onMenuOpen}><Icon name="menu" /></button>
                        <button className="brand" type="button" aria-label="Pitango Market" onClick={handleBrandClick} onKeyDown={handleBrandKeyDown}>
                            <img className="brand__logo" src={assetUrl("images/pitango-logo.png")} alt="Pitango Market" />
                        </button>
                        <SearchBox locale={props.locale} placeholder={props.t("layout.header.searchPlaceholder")} query={props.search} results={props.searchResults} t={props.t} onChange={props.onSearchChange} onSelect={props.onProductSelect} />
                        <div className="header-actions">
                            <LanguagePicker locale={props.locale} label={props.t("layout.header.language")} onChange={props.onLocaleChange} />
                            <div className="notifications-menu-wrap">
                                {user ? <button
                                    ref={notificationsButtonRef}
                                    className="header-btn notifications-btn"
                                    type="button"
                                    aria-label={props.t("layout.header.notifications")}
                                    aria-haspopup="dialog"
                                    aria-expanded={notificationsOpen}
                                    aria-controls={notificationsMenuId}
                                    onClick={() => {
                                        setAccountOpen(false);
                                        setNotificationsOpen(value => !value);
                                    }}
                                ><Icon name="bell" /></button> : <Link className="header-btn notifications-btn" to={`/${props.locale}/notifications`} aria-label={props.t("layout.header.notifications")}><Icon name="bell" /></Link>}
                                {user && notificationsOpen ? <div ref={notificationsMenuRef} id={notificationsMenuId} className="notifications-menu" role="dialog" aria-label={props.t("pages.notifications.title")}>
                                    <div className="notifications-menu__head">
                                        <strong>{props.t("pages.notifications.title")}</strong>
                                    </div>
                                    <div className="notifications-menu__items">
                                        {notificationsQuery.isPending ? <p className="notifications-menu__status">{props.t("pages.loading")}</p> : notificationsQuery.isError ? <p className="notifications-menu__status notifications-menu__status--error">{props.t("pages.error")}</p> : recentNotifications.length ? recentNotifications.map(notification => (
                                            <article className="notifications-menu__item" key={notification.notificationId}>
                                                <strong>{notification.subject || props.t("pages.notifications.noSubject")}</strong>
                                                {notification.body ? <span>{notification.body}</span> : null}
                                                <time>{new Intl.DateTimeFormat(props.locale, { dateStyle: "medium" }).format(new Date(notification.createdAt))}</time>
                                            </article>
                                        )) : <p className="notifications-menu__status">{props.t("pages.notifications.empty")}</p>}
                                    </div>
                                    <Link className="notifications-menu__all" to={`/${props.locale}/notifications`} onClick={() => setNotificationsOpen(false)}>{props.t("pages.notifications.showAll")}</Link>
                                </div> : null}
                            </div>
                            <div className="account-menu-wrap" ref={accountMenuRef}>
                                <button
                                    ref={accountButtonRef}
                                    className="header-btn user-btn"
                                    type="button"
                                    aria-label={props.t("layout.header.account")}
                                    aria-haspopup="menu"
                                    aria-expanded={accountOpen}
                                    aria-controls="account-menu"
                                    onClick={() => setAccountOpen(value => !value)}
                                    title={user ? user.displayName ?? props.t("layout.header.account") : props.t("layout.header.account")}
                                >
                                    <Icon name="user" />
                                    <span className="header-copy">
                                        <small>{user ? props.t("layout.header.hello") : ""}</small>
                                        <strong>{user ? user.displayName : props.t("layout.header.account")}</strong>
                                    </span>
                                </button>
                                <PopupMenu
                                    open={accountOpen}
                                    onClose={() => setAccountOpen(false)}
                                    anchorRef={accountButtonRef}
                                    id="account-menu"
                                    placement="bottom"
                                    align="end"
                                    offset={Math.max(8, menuOffset)}
                                    preferPlacement
                                    className="user-menu"
                                    itemClassName="user-menu__item"
                                    width={176}
                                    minWidth={176}
                                    maxWidth={176}
                                    submenuPlacement={submenuPlacement}
                                    submenuOffset={Math.max(2, menuOffset * 0.5)}
                                    items={accountMenuItems.map(item => ({
                                        ...item,
                                        disabled: item.id === "sign-out" ? signingOut : item.disabled,
                                    }))}
                                />
                            </div>
                            {user ? <button className="cart-btn" type="button" onClick={props.onCartOpen} aria-label={props.t("commerce.cart.title")} aria-controls="cart-drawer" aria-expanded={props.cartOpen}>
                                <Icon name="cart" /><span className="header-copy"><small>{props.t("layout.header.cartItems", { count: props.cartCount })}</small><strong dir="ltr">{formatCurrency(props.cartTotal, props.locale)}</strong></span><span className="cart-count">{props.cartCount}</span>
                            </button> : null}
                        </div>
                    </div>
                    <div className="filters">
                        {filters.map((key, index) => {
                            const category = key.startsWith("category:") ? primaryCategories.find(item => item.id === key.slice("category:".length)) : undefined;
                            const destination = key === "discover" ? `/${props.locale}` : key === "realProducts" ? `/${props.locale}/products` : key === "newArrivals" ? `/${props.locale}/new-arrivals` : key === "offers" ? `/${props.locale}/special-offers` : category ? `/${props.locale}/products/${category.id}` : null;
                            const active = destination === location.pathname || (index === 0 && location.pathname === `/${props.locale}`);
                            const label = category ? category.name : key === "discover" ? props.t("layout.sidebar.homePage") : props.t(`layout.filters.${key}`);
                            return <button key={key} className={`filter-chip${active ? " active" : ""}${key === "offers" ? " sale" : ""}`} type="button" onClick={destination ? () => navigate(destination) : undefined}>{label}</button>;
                        })}
                    </div>
                    {props.belowFilters ? <div className="header__below-filters">{props.belowFilters}</div> : null}
                </div>
            </header>
        </>
    );
}
